import { ApiError, apiFetch } from '@/lib/apiFetch';
import { useCallback, useEffect, useRef, useState } from 'react';

type RunStatus = 'running' | 'done' | 'failed';

type RunRow = {
  id: number;
  started_at: number;
  finished_at: number | null;
  trigger: 'cron' | 'manual';
  triggered_by: string | null;
  total: number | null;
  status: RunStatus;
  error_message: string | null;
};

type RunListRow = RunRow & { checked: number; deleted: number; failed: number };

type RunItem = {
  id: number;
  run_id: number;
  eshop_code: string;
  availability_name: string;
  term_date: string;
  ok: number;
  deleted: number;
  variants_reset: number;
  variants_still_in_production: number;
  error_message: string | null;
  created_at: number;
};

type RunPostResponse = { run: RunRow; items: RunItem[]; done: boolean };

const COUNTRY_FLAG: Record<string, string> = { 'v-mart-cz': '🇨🇿', 'v-mart-sk': '🇸🇰' };

/**
 * Záložky přibývají postupně, jak se sem stěhují další automatizace —
 * zatím jediná (úklid propadlých dostupností), ale struktura na to počítá.
 */
const AUTOMATIZACE_TABS = [
  {
    id: 'propadle-dostupnosti',
    label: 'Mazání starých výrobních termínů + dostupností produktů v Shoptetu',
  },
] as const;
type AutomatizaceTab = (typeof AUTOMATIZACE_TABS)[number]['id'];

/** Unix ms → „22.9.2026 10:15". */
function fmtDateTime(ms: number): string {
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getDate()}.${d.getMonth() + 1}.${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/**
 * Automatizace: úklid propadlých termínových dostupností v Shoptetu (CZ i SK).
 * Termínové dostupnosti („Ve výrobě - odešleme nejpozději…") mají v Shoptetu
 * limit 50 celkem — stejně jako stavy objednávek. Tenhle běh najde ty, co
 * dávno proběhly, přepne jejich varianty (pokud na ně už nic nečeká ve
 * výrobě) zpátky na Skladem/Skladom a dostupnost smaže, ať je pod limitem
 * pořád místo. Běží i automaticky každou noc (cron ve 2:00 UTC).
 */
export function AutomatizacePage() {
  const [tab, setTab] = useState<AutomatizaceTab>(AUTOMATIZACE_TABS[0].id);
  const [runs, setRuns] = useState<RunListRow[] | null>(null);
  const [active, setActive] = useState<{ run: RunRow; items: RunItem[] } | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Zabrání dvěma souběžným smyčkám (např. dvojklik) a doběhne, i když
  // uživatel mezitím klikne na jiný běh v historii.
  const runningRunId = useRef<number | null>(null);

  const loadRuns = useCallback(async () => {
    try {
      const d = await apiFetch<{ runs: RunListRow[] }>('/api/vyroba/terms/automatizace/runs');
      setRuns(d.runs);
    } catch (e: unknown) {
      setError(e instanceof ApiError ? e.describe() : 'Načtení historie selhalo');
    }
  }, []);

  useEffect(() => {
    loadRuns();
  }, [loadRuns]);

  /** Volá `/run` v dávkách, dokud backend neřekne `done: true`. Vždycky jen
   * jeden dokončený request najednou — žádná práce nezůstává viset na pozadí. */
  async function runLoop(runId?: number) {
    if (runningRunId.current !== null) return; // už jedna smyčka běží
    setRunning(true);
    setError(null);
    let id = runId;
    let done = false;
    try {
      do {
        const d = await apiFetch<RunPostResponse>('/api/vyroba/terms/automatizace/run', {
          method: 'POST',
          body: id ? { run_id: id } : {},
        });
        id = d.run.id;
        runningRunId.current = id;
        setActive({ run: d.run, items: d.items });
        done = d.done;
      } while (!done);
      await loadRuns();
    } catch (e: unknown) {
      setError(e instanceof ApiError ? e.describe() : 'Úklid selhal');
    } finally {
      runningRunId.current = null;
      setRunning(false);
    }
  }

  async function showRun(id: number) {
    try {
      const d = await apiFetch<{ run: RunRow; items: RunItem[] }>(
        `/api/vyroba/terms/automatizace/runs/${id}`,
      );
      setActive(d);
    } catch (e: unknown) {
      setError(e instanceof ApiError ? e.describe() : 'Načtení běhu selhalo');
    }
  }

  return (
    <>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-text">Automatizace</h1>
        <p className="text-sm text-text-secondary mt-0.5 max-w-3xl">
          Nástroje, co za tebe pravidelně (nebo na požádání) uklízí věci v Shoptetu. Přibývat budou
          postupně, každý má vlastní záložku.
        </p>
      </div>

      <div className="flex flex-wrap gap-2 mb-5">
        {AUTOMATIZACE_TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`rounded-lg px-5 py-3 text-[15px] font-bold transition-colors cursor-pointer ${
              tab === t.id
                ? 'bg-action text-white'
                : 'bg-bg border border-border text-text-secondary hover:border-text-muted'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'propadle-dostupnosti' && (
        <>
          <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
            <p className="text-sm text-text-secondary max-w-3xl">
              Termínové dostupnosti v Shoptetu („Ve výrobě - odešleme nejpozději…") mají dohromady
              limit <strong>50 celkem</strong>, stejně jako stavy objednávek. Termín, co dávno
              proběhl, ale varianta na jeho dostupnosti pořád visí, brání založení nových. Běh najde
              takové dostupnosti, přepne varianty (pokud na ně už nic nečeká ve výrobě) na
              Skladem/Skladom a dostupnost smaže — pro CZ i SK. Běží i automaticky každou noc ve
              2:00 UTC (~3:00 v ČR).
            </p>
            <button
              type="button"
              onClick={() => runLoop()}
              disabled={running}
              className="bg-action hover:bg-action-dark disabled:opacity-50 text-white text-sm font-bold rounded-lg px-5 py-2.5 cursor-pointer transition-colors shrink-0"
            >
              {running ? 'Probíhá…' : 'Uklidit vše'}
            </button>
          </div>

          {error && (
            <div className="text-sm text-danger bg-danger-light border border-danger/30 rounded-lg px-4 py-3 mb-4">
              {error}
            </div>
          )}

          {active && (
            <div className="bg-surface rounded-xl shadow-card px-5 py-4 mb-5">
              <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                <div className="text-sm font-bold text-text">
                  Běh z {fmtDateTime(active.run.started_at)}
                  {active.run.trigger === 'cron' && (
                    <span className="ml-2 text-[11px] font-semibold text-text-muted uppercase">
                      cron
                    </span>
                  )}
                </div>
                <StatusPill status={active.run.status} />
              </div>

              <div className="text-sm text-text-secondary mb-3">
                {active.run.total === null ? (
                  'Zjišťuji, kolik dostupností je propadlých…'
                ) : (
                  <>
                    Zpracováno {active.items.length} / {active.run.total}
                    {active.items.length > 0 && (
                      <>
                        {' '}
                        · smazáno {active.items.filter((i) => i.deleted).length} · chyba{' '}
                        {active.items.filter((i) => !i.ok).length}
                      </>
                    )}
                  </>
                )}
              </div>

              {active.run.error_message && (
                <div className="text-sm text-danger mb-3">{active.run.error_message}</div>
              )}

              {active.items.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-bg text-left text-xs font-semibold text-text-secondary">
                        <th className="px-3 py-2">E-shop</th>
                        <th className="px-3 py-2">Dostupnost</th>
                        <th className="px-3 py-2">Termín</th>
                        <th className="px-3 py-2 text-right">Přepnuto</th>
                        <th className="px-3 py-2 text-right">Pořád ve výrobě</th>
                        <th className="px-3 py-2">Výsledek</th>
                      </tr>
                    </thead>
                    <tbody>
                      {active.items.map((it) => (
                        <tr key={it.id} className="border-b border-border-light">
                          <td className="px-3 py-1.5 whitespace-nowrap">
                            <span aria-hidden>{COUNTRY_FLAG[it.eshop_code] ?? '🏳️'}</span>
                          </td>
                          <td className="px-3 py-1.5 text-text">{it.availability_name}</td>
                          <td className="px-3 py-1.5 text-text-secondary whitespace-nowrap">
                            {it.term_date}
                          </td>
                          <td className="px-3 py-1.5 text-right text-text-secondary">
                            {it.variants_reset}
                          </td>
                          <td className="px-3 py-1.5 text-right text-text-secondary">
                            {it.variants_still_in_production}
                          </td>
                          <td className="px-3 py-1.5">
                            {!it.ok ? (
                              <span
                                className="text-sm font-semibold text-danger"
                                title={it.error_message ?? ''}
                              >
                                ✕ {it.error_message ?? 'chyba'}
                              </span>
                            ) : it.deleted ? (
                              <span className="text-sm font-semibold text-success">✓ smazáno</span>
                            ) : (
                              <span
                                className="text-sm text-text-muted"
                                title={it.error_message ?? ''}
                              >
                                zůstává{it.error_message ? ` — ${it.error_message}` : ''}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          <div className="bg-surface rounded-xl shadow-card overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-bg text-left text-xs font-semibold text-text-secondary">
                  <th className="px-4 py-2.5">Kdy</th>
                  <th className="px-4 py-2.5">Spustil</th>
                  <th className="px-4 py-2.5">Stav</th>
                  <th className="px-4 py-2.5 text-right">Zkontrolováno</th>
                  <th className="px-4 py-2.5 text-right">Smazáno</th>
                  <th className="px-4 py-2.5 text-right">Chyby</th>
                  <th className="px-4 py-2.5" />
                </tr>
              </thead>
              <tbody>
                {runs == null ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-6 text-center text-text-muted">
                      Načítám…
                    </td>
                  </tr>
                ) : runs.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-6 text-center text-text-muted">
                      Zatím žádný běh. Klikni „Uklidit vše".
                    </td>
                  </tr>
                ) : (
                  runs.map((r) => (
                    <tr key={r.id} className="border-b border-border-light">
                      <td className="px-4 py-1.5 text-text-secondary whitespace-nowrap">
                        {fmtDateTime(r.started_at)}
                      </td>
                      <td className="px-4 py-1.5 text-text-secondary">
                        {r.trigger === 'cron' ? 'cron (noční)' : (r.triggered_by ?? 'ručně')}
                      </td>
                      <td className="px-4 py-1.5">
                        <StatusPill status={r.status} />
                      </td>
                      <td className="px-4 py-1.5 text-right text-text-secondary">
                        {r.checked}
                        {r.total !== null && r.total !== r.checked && ` / ${r.total}`}
                      </td>
                      <td className="px-4 py-1.5 text-right text-text-secondary">{r.deleted}</td>
                      <td className="px-4 py-1.5 text-right text-text-secondary">
                        {r.failed > 0 ? (
                          <span className="text-danger font-semibold">{r.failed}</span>
                        ) : (
                          0
                        )}
                      </td>
                      <td className="px-4 py-1.5 text-right">
                        <button
                          type="button"
                          onClick={() => (r.status === 'running' ? runLoop(r.id) : showRun(r.id))}
                          disabled={running}
                          className="text-sm font-semibold text-primary hover:underline cursor-pointer disabled:opacity-50"
                        >
                          {r.status === 'running' ? 'Pokračovat' : 'Detail'}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}

function StatusPill({ status }: { status: RunStatus }) {
  if (status === 'running') {
    return (
      <span className="text-[11px] font-semibold text-primary bg-primary-50 px-2 py-0.5 rounded">
        probíhá…
      </span>
    );
  }
  if (status === 'failed') {
    return (
      <span className="text-[11px] font-semibold text-danger bg-danger-light px-2 py-0.5 rounded">
        chyba
      </span>
    );
  }
  return (
    <span className="text-[11px] font-semibold text-success bg-success-light px-2 py-0.5 rounded">
      hotovo
    </span>
  );
}
